/**
 * @fileoverview WebSocket client for the 'Rasca y gana' (Scratcher) Admin Operator Panel (Tile ID 43).
 * Connects to the platform's real-time communication channel to receive telemetry,
 * snapshots, game state transitions, countdowns, and broadcast operator commands.
 * 
 * @module utilities/WS/Admin/ScratcherWS
 */

let scratcherAdminWebSocket = null;
let isScratcherAdminConnected = false;
let reconnectionTimeoutIdentifier = null;
let connectionAttemptCounter = 0;
const MAXIMUM_RECONNECTION_ATTEMPTS = 15;
const RECONNECTION_DELAY_MILLISECONDS = 3000;

/**
 * Establishes a persistent WebSocket connection for the Scratcher admin panel.
 * 
 * @function ConnectWebSocketForScratcherAdmin
 * @param {Function} [onMessageReceived=()=>{}] - Callback invoked whenever a message is received.
 * @param {Function} [onStatusChange=()=>{}] - Callback invoked when the connection status changes ('connecting', 'connected', 'reconnecting', 'error', 'disconnected').
 */
export const ConnectWebSocketForScratcherAdmin = (
    onMessageReceived = () => {},
    onStatusChange = () => {}
) => {
    if (scratcherAdminWebSocket || isScratcherAdminConnected) {
        return;
    }

    const establishConnection = () => {
        if (connectionAttemptCounter >= MAXIMUM_RECONNECTION_ATTEMPTS) {
            onStatusChange('error');
            return;
        }

        connectionAttemptCounter += 1;
        onStatusChange('connecting');

        try {
            const webSocketEndpointUrl = `${process.env.REACT_APP_API_URL_WSS}?tipo=superadmin`;
            scratcherAdminWebSocket = new WebSocket(webSocketEndpointUrl);

            scratcherAdminWebSocket.onopen = () => {
                isScratcherAdminConnected = true;
                connectionAttemptCounter = 0;
                onStatusChange('connected');
            };

            scratcherAdminWebSocket.onclose = () => {
                isScratcherAdminConnected = false;
                scratcherAdminWebSocket = null;
                onStatusChange('reconnecting');

                clearTimeout(reconnectionTimeoutIdentifier);
                reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
            };

            scratcherAdminWebSocket.onerror = () => {
                onStatusChange('error');
            };

            scratcherAdminWebSocket.onmessage = (messageEvent) => {
                try {
                    const parsedPayload = JSON.parse(messageEvent.data);
                    onMessageReceived(parsedPayload);
                } catch (parseError) {
                    // Ignore non-JSON or unformatted packets
                }
            };
        } catch (connectionError) {
            onStatusChange('error');
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
        }
    };

    establishConnection();
};

/**
 * Sends a JSON payload through the active Scratcher admin WebSocket connection.
 * 
 * @function SendWebSocketMessage
 * @param {Object} payloadObject - JSON object to transmit.
 * @returns {boolean} True if successfully sent, false otherwise.
 */
export const SendWebSocketMessage = (payloadObject) => {
    try {
        if (scratcherAdminWebSocket && scratcherAdminWebSocket.readyState === WebSocket.OPEN) {
            scratcherAdminWebSocket.send(JSON.stringify(payloadObject));
            return true;
        }
        return false;
    } catch (sendError) {
        return false;
    }
};

/**
 * Closes and cleans up the Scratcher admin WebSocket connection.
 * 
 * @function DisconnectWebSocketForScratcherAdmin
 */
export const DisconnectWebSocketForScratcherAdmin = () => {
    try {
        if (reconnectionTimeoutIdentifier) {
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = null;
        }

        if (scratcherAdminWebSocket) {
            scratcherAdminWebSocket.onopen = null;
            scratcherAdminWebSocket.onclose = null;
            scratcherAdminWebSocket.onerror = null;
            scratcherAdminWebSocket.onmessage = null;

            if (scratcherAdminWebSocket.readyState === WebSocket.OPEN) {
                scratcherAdminWebSocket.close();
            } else if (scratcherAdminWebSocket.readyState === WebSocket.CONNECTING) {
                const socketToClose = scratcherAdminWebSocket;
                socketToClose.onopen = () => {
                    try {
                        socketToClose.close();
                    } catch {
                        // Safe ignore
                    }
                };
            }

            scratcherAdminWebSocket = null;
            isScratcherAdminConnected = false;
        }
    } catch (teardownError) {
        // Safe teardown
    }
};
